import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { GetMyDueItemsChangeRequestsResponseRow } from '@risksmart-app/drizzle/src/dto';
import type { OwnershipFlags } from '@risksmart-app/trpc/src/routers/frontend/shared/ownership-filter-input';
import type { MyDueItemsResponse } from '@risksmart-app/trpc/src/types';
import {
  GetMyDueItemsDocument,
  type GetMyDueItemsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { getQueryVariables } from '@/utils/myItemsFilterConditions';

import { callerOwnerGrants } from '../change-request/callerOwnerGrants';

// COI-817 — the due table intentionally renders raw `AttestationStatus` instead
// of the related status object.
type MyDueItemsSelectionGaps = 'attestation_record.attestationRecordStatus';

export const mapDueChangeRequestToGraphQL = ({
  AcceptanceId,
  ...changeRequest
}: GetMyDueItemsChangeRequestsResponseRow) => {
  const currentUserOwners = callerOwnerGrants(
    changeRequest.currentUserOwnerList
  );
  const parent = changeRequest.parent;

  return {
    ...changeRequest,
    RequestedChanges: AcceptanceId ? { Id: AcceptanceId } : null,
    parent: parent
      ? {
          ...parent,
          currentUserOwners,
          documentFile: parent.documentFile
            ? {
                ...parent.documentFile,
                parent: parent.documentFile.parent
                  ? { ...parent.documentFile.parent, currentUserOwners }
                  : null,
              }
            : null,
          issue_assessment: parent.issue_assessment
            ? {
                ...parent.issue_assessment,
                parent: parent.issue_assessment.parent
                  ? { ...parent.issue_assessment.parent, currentUserOwners }
                  : null,
              }
            : null,
          acceptance: parent.acceptance
            ? {
                ...parent.acceptance,
                // The read does not traverse an acceptance to its risks, so
                // one standing risk carries the grants the walk looks for.
                parents: [{ risk: { currentUserOwners } }],
              }
            : null,
        }
      : null,
  };
};

export const mapTrpcMyDueItemsToGraphQL = (
  trpcData: MyDueItemsResponse
): StrictMappedGraphQLSelection<
  GetMyDueItemsQuery,
  MyDueItemsSelectionGaps
> => {
  return {
    action: trpcData.action,
    assessment: trpcData.assessment,
    control: trpcData.control,
    document: trpcData.document,
    indicator: trpcData.indicator,
    issue: trpcData.issue,
    obligation: trpcData.obligation,
    risk: trpcData.risk,
    assessment_activity: trpcData.assessmentActivity,
    attestation_record: trpcData.attestationRecord,
    change_request: trpcData.changeRequest.map((cr) => {
      const mappedChangeRequest = mapDueChangeRequestToGraphQL(cr);

      return {
        ...mappedChangeRequest,
        responses: mappedChangeRequest.responses.map((response) => ({
          ...response,
          approver: response.approver,
        })),
      };
    }),
  };
};

export type UseGetMyDueItemsArgs = {
  /** Pre-resolved UTC ISO instant — the "due by" horizon. */
  date: string;
  /**
   * The My Items 8-flag ownership model. Keeps the procedure's `ownershipFilter`
   * key rather than the shared `ownership` fragment name — renaming the wire is
   * a server change (COI-691 §3).
   */
  ownershipFilter: OwnershipFlags;
};

/**
 * All ten `*FilterConditions` variables are non-null in `getMyDueItems`, so each
 * is supplied on every call.
 *
 * There is no unfiltered call: `ownershipFilter` is required here and on the
 * procedure, and an all-false set yields `{ _or: [] }` — FALSE to Hasura, so an
 * empty feed. Deliberate parity with the tRPC leg, where "not supplied" and
 * "all eight false" are different values (`ownership-filter-input.ts`).
 * Expressing "every due item regardless of ownership" needs the procedure input
 * widened.
 */
const useMyDueItemsGraphqlVariables = ({
  date,
  ownershipFilter,
}: UseGetMyDueItemsArgs) => {
  const { user } = useRisksmartUser();
  const conditions = getQueryVariables(ownershipFilter, user?.userId);

  return {
    userId: user?.userId ?? '',
    date,
    riskFilterConditions: conditions.riskFilterConditions,
    actionFilterConditions: conditions.actionFilterConditions,
    assessmentFilterConditions: conditions.assessmentFilterConditions,
    controlFilterConditions: conditions.controlFilterConditions,
    issueFilterConditions: conditions.issueFilterConditions,
    assessmentActivityFilterConditions:
      conditions.assessmentActivityFilterConditions,
    documentFilterConditions: conditions.documentFilterConditions,
    indicatorFilterConditions: conditions.indicatorFilterConditions,
    obligationFilterConditions: conditions.obligationFilterConditions,
  };
};

const useGetMyDueItemsBase = createQueryHook<
  UseGetMyDueItemsArgs,
  MyDueItemsResponse,
  GetMyDueItemsQuery,
  MyDueItemsSelectionGaps
>({
  trpcQueryOptions: (trpc, { date, ownershipFilter }) =>
    trpc.frontend.myItems.dueItems.queryOptions({
      date,
      ownershipFilter,
    }),
  mapTrpcDataToGraphQL: mapTrpcMyDueItemsToGraphQL,
  graphqlDocument: GetMyDueItemsDocument,
  graphqlVariables: useMyDueItemsGraphqlVariables,
});

/**
 * Fetches the My Items "due by <date>" feed across all ten entity types.
 * The current user is resolved internally — callers pass the horizon and the
 * ownership flags only.
 */
export const useGetMyDueItems = (params: {
  queryArgs: UseGetMyDueItemsArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => useGetMyDueItemsBase(params);
